import { HostAriaLabelMixin, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
// Composition dependencies, not conveniences: an unregistered <schmancy-list-item>
// never upgrades, so its <slot name="trailing"> stays a plain element and the
// consumer's own menu is silently never rendered.
import '../icons'
import '../list'
import '../typography'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { choose } from 'lit/directives/choose.js'
import { when } from 'lit/directives/when.js'

/**
 * The glyph a file's own MIME type earns. Derived from the type the producing
 * system reports, never from the name's extension — an extension is a surface
 * proxy a user can rename and a server never sets.
 */
function glyphFor(kind: string): string {
	const type = kind.trim().toLowerCase()
	if (type === 'folder' || type === 'inode/directory') return 'folder'
	if (type.startsWith('image/')) return 'image'
	if (type === 'application/pdf') return 'picture_as_pdf'
	if (type.startsWith('video/')) return 'movie'
	if (type.startsWith('audio/')) return 'audio_file'
	if (type === 'text/csv' || type.includes('spreadsheet') || type.includes('excel')) return 'table_chart'
	if (type.includes('zip') || type.includes('compressed') || type.includes('tar')) return 'folder_zip'
	if (type.startsWith('text/') || type.includes('document') || type.includes('word')) return 'description'
	return 'draft'
}

/**
 * @element schmancy-file-item
 *
 * One file or folder with its name, summary, and an independent trailing control.
 * A card with slotted preview content places its name above a 4:3 content preview.
 *
 * @slot leading - Replaces the kind glyph (a thumbnail, an avatar, a checkbox).
 * @slot preview - Non-interactive file content, shown full-width in card layout.
 * @slot summary - Non-interactive summary content; replaces the summary string in either layout.
 * @slot trailing - The row's own control — a menu, an icon button, a badge.
 * @fires click - Native, from the activatable row/card. Absent while `readonly`.
 */
@schmancyElement('schmancy-file-item')
export class SchmancyFileItem extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
			:host([layout='card']) {
				height: 100%;
			}
			[part='preview'] {
				flex: 1 0 auto;
				aspect-ratio: 4 / 3;
				width: 100%;
				overflow: hidden;
			}
		`,
	]

	/** The file or folder's own name. Also the row's accessible name. */
	@property({ type: String }) name = ''

	/** Hides the visible filename while retaining the accessible name. */
	@property({ type: Boolean, attribute: 'hide-name' }) hideName = false

	/** Fallback under the name when no summary content is slotted. */
	@property({ type: String }) summary = ''

	/** MIME type, or the literal `folder`. Selects the glyph; never parsed from the name. */
	@property({ type: String }) kind = ''

	/** `list` — a row in a file list. `card` — a tile in a file grid. */
	@property({ type: String, reflect: true }) layout: 'list' | 'card' = 'list'

	@property({ type: Boolean, reflect: true }) selected = false

	/** Removes list-row padding when the surrounding container supplies spacing. */
	@property({ type: Boolean, reflect: true }) flush = false

	/** Withdraws activation: no pointer cursor, no tab stop, no click. */
	@property({ type: Boolean, reflect: true }) readonly = false

	@state() private hasPreview = false

	override connectedCallback(): void {
		super.connectedCallback()
		// The <ul> lives in schmancy-list's shadow root and this host is what is
		// slotted into it, so the host — not the row this component renders — is
		// the element the list owns as its item. Standalone (a lone file card in
		// a grid) it claims nothing: an orphan listitem with no list is worse
		// than a plain button.
		if (!this.hasAttribute('role') && this.closest('schmancy-list')) this.setAttribute('role', 'listitem')
	}

	override render() {
		const glyph = html`<slot name="leading"><schmancy-icon>${glyphFor(this.kind)}</schmancy-icon></slot>`

		return choose(
			this.layout,
			[
				[
					'card',
					() => html`
						<div class="relative grid h-full">
							<button
								type="button"
								aria-label=${this.ariaLabel || this.name}
								aria-current=${this.selected ? 'true' : 'false'}
								?disabled=${this.readonly}
								class=${this.classMap({
									'h-full w-full rounded-xl transition-colors': true,
									'grid justify-items-center gap-2 px-3 py-4 text-center': !this.hasPreview,
									'flex flex-col overflow-hidden text-left': this.hasPreview,
									'bg-surface-container': !this.selected,
									'bg-secondary-container': this.selected,
									'hover:bg-surface-containerHigh cursor-pointer': !this.readonly && !this.selected,
									'focus-visible:outline-2 focus-visible:outline-primary-default': !this.readonly,
								})}
							>
								${when(
									!this.hasPreview,
									() => html`<div class="grid place-items-center text-4xl text-surface-onVariant">${glyph}</div>`,
								)}
								${when(!this.hideName, () => html`
									<schmancy-typography
										type="title"
										token="sm"
										class=${this.classMap({
											'w-full truncate': true,
											'px-3 py-3 pr-14 min-h-14': this.hasPreview,
										})}
									>
										${this.name}
									</schmancy-typography>
								`)}
								<div
									part="preview"
									class=${this.classMap({
										'bg-surface-containerLow pointer-events-none': this.hasPreview,
										hidden: !this.hasPreview,
									})}
								>
									<slot
										name="preview"
										@slotchange=${(event: Event) => {
											if (event.currentTarget instanceof HTMLSlotElement)
												this.hasPreview = event.currentTarget.assignedElements({ flatten: true }).length > 0
										}}
									></slot>
								</div>
								<slot name="summary">${when(
									!!this.summary,
									() => html`
										<schmancy-typography
											type="body"
											token="xs"
											class=${this.classMap({
												'w-full truncate text-surface-onVariant': true,
												'px-3 py-3': this.hasPreview,
											})}
										>
											${this.summary}
										</schmancy-typography>
									`,
								)}</slot>
							</button>
							<div class="absolute top-1 right-1 z-1"><slot name="trailing"></slot></div>
						</div>
					`,
				],
			],
			() => html`
				<schmancy-list-item ?readonly=${this.readonly} ?selected=${this.selected} ?flush=${this.flush} .label=${this.ariaLabel || this.name}>
					<span slot="leading" class="grid place-items-center text-surface-onVariant">${glyph}</span>
					<span class="grid min-w-0">
						${when(!this.hideName, () => html`<schmancy-typography type="body" token="md" class="truncate">${this.name}</schmancy-typography>`)}
						<slot name="summary">${when(
							!!this.summary,
							() => html`
								<schmancy-typography type="body" token="xs" class="truncate text-surface-onVariant">
									${this.summary}
								</schmancy-typography>
							`,
						)}</slot>
					</span>
					<span slot="trailing"><slot name="trailing"></slot></span>
				</schmancy-list-item>
			`,
		)
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-file-item': SchmancyFileItem
	}
}
